import React from 'react';
import { Loader2, ShieldCheck, ShieldAlert, ShieldX, ExternalLink, RefreshCw } from 'lucide-react';

const STATUS_META = {
  pass: { label: 'Pass', icon: ShieldCheck, color: 'text-green-400 border-green-400/40 bg-green-400/5' },
  review: { label: 'Review', icon: ShieldAlert, color: 'text-yellow-400 border-yellow-400/40 bg-yellow-400/5' },
  fail: { label: 'Fail', icon: ShieldX, color: 'text-red-400 border-red-400/40 bg-red-400/5' },
  not_found: { label: 'Not found', icon: ShieldAlert, color: 'text-white/50 border-white/20' },
  error: { label: 'Error', icon: ShieldX, color: 'text-red-400 border-red-400/40 bg-red-400/5' },
};

const RISK_META = {
  low: 'text-green-400',
  medium: 'text-yellow-400',
  high: 'text-red-400',
  review: 'text-white/50',
};

function Row({ label, children }) {
  return (
    <div>
      <p className="text-white/30 text-[10px] font-bold tracking-widest mb-0.5">{label}</p>
      <p className="text-white/70 text-xs">{children}</p>
    </div>
  );
}

function yn(v) {
  if (v === true) return '✓ Yes';
  if (v === false) return '✗ No';
  return '—';
}

export default function B2BCreditCheckPanel({ account, checking, onRun }) {
  const cc = account.credit_check;
  const meta = cc ? (STATUS_META[cc.status] || STATUS_META.error) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <p className="text-primary text-[10px] font-bold tracking-widest">COMPANY CREDIT CHECK</p>
          {meta && (
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-bold tracking-widest uppercase ${meta.color}`}>
              <meta.icon className="w-3.5 h-3.5" /> {meta.label}
            </span>
          )}
          {cc?.risk_band && (
            <span className={`text-[10px] font-bold tracking-widest uppercase ${RISK_META[cc.risk_band] || 'text-white/50'}`}>
              Risk: {cc.risk_band}
            </span>
          )}
          {cc?.source && (
            <span className="text-white/30 text-[10px] font-bold tracking-widest uppercase">
              {cc.source === 'companies_house' ? 'Companies House' : cc.source === 'creditsafe' ? 'Creditsafe' : cc.source}
            </span>
          )}
          {cc?.checked_at && (
            <span className="text-white/20 text-[10px]">checked {new Date(cc.checked_at).toLocaleString('en-GB')}</span>
          )}
        </div>
        <button
          onClick={onRun}
          disabled={checking}
          className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
        >
          {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {cc ? 'RE-RUN CHECK' : 'RUN CHECK'}
        </button>
      </div>

      {!cc && !checking && (
        <p className="text-white/30 text-xs">No credit check on file yet. Click <span className="text-primary font-bold">Run check</span> to verify this company against Companies House.</p>
      )}

      {checking && !cc && (
        <div className="flex items-center gap-2 text-white/40 text-xs"><Loader2 className="w-4 h-4 animate-spin" /> Checking Companies House…</div>
      )}

      {cc?.summary && <p className="text-white/60 text-xs leading-relaxed">{cc.summary}</p>}

      {cc && cc.status !== 'not_found' && cc.status !== 'error' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Row label="COMPANY NUMBER">{cc.company_number || '—'}</Row>
          <Row label="REGISTERED NAME">{cc.company_name || '—'}</Row>
          <Row label="STATUS">{cc.company_status || '—'}</Row>
          <Row label="TYPE">{cc.company_type || '—'}</Row>
          <Row label="INCORPORATED">{cc.incorporation_date || '—'}</Row>
          <Row label="NAME MATCH">{yn(cc.company_name_match)}</Row>
          <Row label="ADDRESS MATCH">{cc.address_match === null ? 'Unknown' : yn(cc.address_match)}</Row>
          <Row label="ACCOUNTS OVERDUE">{cc.accounts_overdue ? '⚠ Yes' : 'No'}</Row>
          <Row label="CONFIRMATION OVERDUE">{cc.confirmation_overdue ? '⚠ Yes' : 'No'}</Row>
          <Row label="INSOLVENCY HISTORY">{cc.has_insolvency_history ? '⚠ Yes' : 'No'}</Row>
          <Row label="CHARGES">{cc.has_charges ? 'Yes' : 'No'}</Row>
          <Row label="ACCOUNTS NEXT DUE">{cc.accounts_next_due || '—'}</Row>
        </div>
      )}

      {cc?.credit_score != null && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Row label="CREDIT SCORE">{cc.credit_score}</Row>
          <Row label="CREDIT RATING">{cc.credit_rating || '—'}</Row>
          <Row label="RECOMM. LIMIT">{cc.credit_limit_gbp != null ? `£${Number(cc.credit_limit_gbp).toLocaleString()}` : '—'}</Row>
        </div>
      )}

      {cc?.registered_office && <Row label="REGISTERED OFFICE">{cc.registered_office}</Row>}

      {cc?.flags?.length > 0 && (
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1">FLAGS</p>
          <ul className="space-y-1">
            {cc.flags.map((fl, i) => (
              <li key={i} className="text-yellow-400 text-xs flex items-start gap-1.5"><span>•</span>{fl}</li>
            ))}
          </ul>
        </div>
      )}

      {cc?.company_number && (
        <a href={`https://find-and-update.company-information.service.gov.uk/company/${cc.company_number}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 border border-white/15 hover:border-white/30 text-white/50 hover:text-white text-[10px] font-bold tracking-widest px-3 py-2 transition-colors">
          <ExternalLink className="w-3.5 h-3.5" /> VIEW ON COMPANIES HOUSE
        </a>
      )}
    </div>
  );
}